/*
 * The PDP campaign banner loop: started, never autoplayed.
 *
 * The `<video>` in nasty-g-pdp-story.liquid deliberately ships without an `autoplay`
 * attribute. The alternative, autoplay plus a pause on load when the reduced motion query
 * matches, fails in two directions that matter. It makes a visitor who has asked their
 * operating system for less motion depend on this file parsing and running before they get
 * it, and until it does they watch the thing they opted out of; and it makes `preload="none"`
 * a lie, because a browser told to autoplay fetches the media regardless, which puts 4.7 MB
 * on the wire during first render on the page PRODUCT.md names as the one that must not do
 * that. Without the attribute the resting state is a poster on a video element that has
 * requested no bytes, and motion is something added on top once two conditions are met.
 * The failure direction is a still frame, which is the safe one: no JavaScript, a syntax
 * error, a blocked script, all land on the poster.
 *
 * Condition one is the visitor's own preference, read live rather than once, so that
 * toggling it in system settings takes effect on the open page instead of at the next
 * reload.
 *
 * Condition two is the band being near the viewport. The banner sits below the buy block
 * and most sessions never reach it, so `play()` is what triggers the download and the
 * observer is what decides when that is worth doing. Leaving the viewport pauses it again:
 * a decoder running on a band nobody is looking at is battery spent on nothing.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const onscreen = new Set();

const sync = () => {
  for (const video of onscreen) {
    if (REDUCED.matches) {
      video.pause();
    } else {
      // Muted and playsinline, so this is allowed without a gesture. It can still be
      // rejected, by a data saver or a power saving mode, and the poster stands if it is.
      video.play().catch(() => {});
    }
  }
};

const watcher = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        onscreen.add(entry.target);
      } else {
        onscreen.delete(entry.target);
        entry.target.pause();
      }
    }
    sync();
  },
  // 200px of warning, so decoding starts a little before the band is scrolled into the
  // viewport rather than at the moment it is read.
  { rootMargin: '200px 0px' }
);

const attach = () => {
  for (const video of document.querySelectorAll('.nasty-g-pdp-story__banner-video')) {
    watcher.observe(video);
  }
};

attach();

// A module is evaluated once per URL, so the theme editor swapping this section in brings a new
// `<video>` that nothing is watching. Same re-scan the reveal helper in nasty-global.js does.
document.addEventListener('shopify:section:load', attach);

REDUCED.addEventListener('change', sync);
